import {
  type ButtonHTMLAttributes,
  Children,
  cloneElement,
  isValidElement,
  type KeyboardEventHandler,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "./i18n";
import SelectMenu from "./SelectMenu";

export default function ToolPageHeader({
  title,
  titleBadge,
  help,
  navigation,
  sessionStatus,
  continued = false,
  children,
}: {
  title: ReactNode;
  titleBadge?: ReactNode;
  help?: "coursework" | "calendar";
  navigation?: ReactNode;
  sessionStatus?: ReactNode;
  continued?: boolean;
  children?: ReactNode;
}) {
  return (
    <header className={`tool-page-header${continued ? " tool-page-header-continued" : ""}`}>
      <div className="tool-page-identity">
        <div className="tool-page-heading">
          <h2 id="page-heading" className="tool-page-title">
            {title}
          </h2>
          {titleBadge && <span className="tool-page-title-badge">{titleBadge}</span>}
          {help && <ToolPageHelp key={help} topic={help} />}
        </div>
        {navigation}
        {sessionStatus && <div className="tool-page-status">{sessionStatus}</div>}
      </div>
      {children}
    </header>
  );
}

function ToolPageHelp({ topic }: { topic: "coursework" | "calendar" }) {
  const { t } = useTranslation("toolPage");
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const title = t(`help.${topic}.title`);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: Event) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("focusin", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("focusin", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="tool-page-help" ref={root}>
      <button
        type="button"
        className="tool-page-help-trigger"
        ref={trigger}
        aria-label={title}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="tool-page-help-icon" aria-hidden="true">
          ?
        </span>
      </button>
      {open && (
        <section className="tool-page-help-panel" id={id} aria-labelledby={`${id}-title`}>
          <div className="tool-page-help-heading">
            <strong id={`${id}-title`}>{title}</strong>
            <button
              type="button"
              className="tool-page-help-close"
              aria-label={t("closeHelp")}
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <ul>
            {(["first", "second", "third"] as const).map((item) => (
              <li key={item}>{t(`help.${topic}.${item}`)}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function ToolPageToolbar({
  scope,
  actions,
  children,
}: {
  scope?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="tool-page-toolbar">
      {scope && <div className="tool-page-scope">{scope}</div>}
      {actions && <div className="tool-page-actions">{actions}</div>}
      {children}
    </div>
  );
}

type TabButton = ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;

/** Keep the original buttons and handlers mounted, including overflowed tab-panel labels. */
export function ToolPageNavigation({
  children,
  label,
  role = "tablist",
  onKeyDown,
}: {
  children: ReactNode;
  label: string;
  role?: "tablist" | "group" | "navigation";
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}) {
  const { t } = useTranslation("toolPage");
  const root = useRef<HTMLDivElement>(null);
  const widths = useRef<number[]>([]);
  const buttons = Children.toArray(children).filter(isValidElement) as TabButton[];
  const signature = buttons.map((button) => String(button.props.children)).join("|");
  const [layout, setLayout] = useState({ signature, count: buttons.length });
  const visibleCount = layout.signature === signature ? layout.count : buttons.length;

  useLayoutEffect(() => {
    if (!signature) return;
    const container = root.current;
    if (!container) return;
    widths.current = [];
    const setVisibleCount = (count: number) => {
      setLayout((previous) =>
        previous.signature === signature && previous.count === count ? previous : { signature, count },
      );
    };
    const measure = () => {
      const elements = Array.from(container.querySelectorAll<HTMLButtonElement>(".tool-page-tabs > button"));
      elements.forEach((button, index) => {
        if (!button.hidden) widths.current[index] = button.getBoundingClientRect().width;
      });
      const available = container.clientWidth;
      if (!available) return;
      const total = widths.current.reduce((sum, width) => sum + width + 4, 8);
      if (total <= available) {
        setVisibleCount(elements.length);
        return;
      }
      let used = 8;
      let count = 0;
      // Reserve a readable selected label and a 44px target for the existing select menu.
      while (count < elements.length && used + (widths.current[count] || 120) + 4 + 160 <= available) {
        used += (widths.current[count] || 120) + 4;
        count += 1;
      }
      setVisibleCount(count);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [signature]);

  const selected = buttons.findIndex(
    ({ props }) =>
      props["aria-selected"] === true || props["aria-pressed"] === true || props["aria-current"] === "page",
  );
  const overflow = buttons.slice(visibleCount);

  return (
    <div
      className="tool-page-navigation"
      ref={root}
      role={role === "navigation" ? role : undefined}
      aria-label={role === "navigation" ? label : undefined}
    >
      <div
        className="tool-page-tabs"
        role={role === "navigation" ? undefined : role}
        aria-label={role === "navigation" ? undefined : label}
        hidden={visibleCount === 0}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          requestAnimationFrame(() => {
            const selectedButton = root.current?.querySelector<HTMLButtonElement>(
              '.tool-page-tabs > button[aria-selected="true"]',
            );
            if (selectedButton?.hidden) root.current?.querySelector<HTMLButtonElement>('[role="combobox"]')?.focus();
          });
        }}
      >
        {buttons.map((button, index) => cloneElement(button, { hidden: index >= visibleCount }))}
      </div>
      {overflow.length > 0 && (
        <div className={`tool-page-overflow${selected >= visibleCount ? " is-selected" : ""}`}>
          <SelectMenu
            label={t("overflow")}
            value={selected >= visibleCount ? String(selected) : ""}
            options={[
              ...(selected < visibleCount ? [{ value: "", label: t("more") }] : []),
              ...overflow.map((button, index) => ({
                value: String(index + visibleCount),
                label: String(button.props.children),
              })),
            ]}
            onChange={(value) => {
              if (!value) return;
              root.current?.querySelectorAll<HTMLButtonElement>(".tool-page-tabs > button")[Number(value)]?.click();
            }}
          />
        </div>
      )}
    </div>
  );
}
